/**
 * Placement from canvas JSON
 *
 * The stored placement of a position is the axis-aligned bounding box of
 * its design objects, computed by this one function over the serialized
 * JSON in millimetre space, never from a live canvas (R35). Writers call
 * it on save; readers call `alignCanvasJsonToPlacement` on load to apply a
 * placement that was changed without the JSON (an agent's `set_placement`).
 */

import {
  computeRelativeDesignStateFromJSON,
  SYSTEM_LAYER_NAME_SET,
  type CanvasObjectJSON,
} from './computeDesignState.js';
import { rescaleCanvasObjectsJSON } from './uniformRescale.js';
import {
  PLACEMENT_MM_TOLERANCE,
  placementAreaFromMm,
  placementMmToRelative,
  placementsMmDiffer,
  relativeToPlacementMm,
  type PlacementAreaMm,
  type PlacementMm,
} from '../placement/placementMm.js';

/**
 * The bounding box of all user objects, in millimetres from the area's top
 * left, or null without user objects. `rotation` is the angle of a single
 * rotated object (importers deliver one) and 0 otherwise.
 */
export function placementMmFromCanvasJson(
  json: { objects?: CanvasObjectJSON[] },
  area: PlacementAreaMm
): PlacementMm | null {
  const rel = computeRelativeDesignStateFromJSON(json, {
    widthPx: area.widthMm,
    heightPx: area.heightMm,
    offsetX: 0,
    offsetY: 0,
  });
  if (!rel) return null;
  const userObjects = (json.objects ?? []).filter(
    (obj) => !SYSTEM_LAYER_NAME_SET.has(obj.name ?? '')
  );
  const rotation = userObjects.length === 1 ? (userObjects[0].angle ?? 0) : 0;
  return relativeToPlacementMm(rel, area, rotation);
}

export type AlignResult =
  | { kind: 'unchanged' }
  | { kind: 'transformed'; objects: CanvasObjectJSON[] }
  | { kind: 'rejected'; reason: 'no_objects' | 'ratio_mismatch' };

/** Relative slack of the height check before a placement counts as distorting. */
const RATIO_TOLERANCE_REL = 0.005;

/**
 * Brings the objects to a stored placement that no longer matches their
 * bounding box (R35). Within the tolerance nothing moves; beyond it the
 * objects are transformed uniformly (factor from the width, the height
 * follows the design ratio); a placement whose height does not follow the
 * ratio would distort the design and is rejected.
 */
export function alignCanvasJsonToPlacement(
  json: { objects?: CanvasObjectJSON[] },
  area: PlacementAreaMm,
  placement: PlacementMm
): AlignResult {
  const bbox = placementMmFromCanvasJson(json, area);
  if (!bbox) return { kind: 'rejected', reason: 'no_objects' };
  if (!placementsMmDiffer(bbox, placement)) return { kind: 'unchanged' };

  const ratio = bbox.height / bbox.width;
  const expectedHeight = placement.width * ratio;
  const slack = Math.max(
    PLACEMENT_MM_TOLERANCE,
    expectedHeight * RATIO_TOLERANCE_REL
  );
  if (Math.abs(placement.height - expectedHeight) > slack) {
    return { kind: 'rejected', reason: 'ratio_mismatch' };
  }

  const target = { ...placement, height: expectedHeight };
  const mmArea = placementAreaFromMm(area);
  return {
    kind: 'transformed',
    objects: rescaleCanvasObjectsJSON(
      json.objects ?? [],
      mmArea,
      mmArea,
      placementMmToRelative(target, area)
    ),
  };
}
